/** 处理自定义文件夹结构生成以及对应文件产出的功能 */
import { translateLang } from '@/data/i18n/translateLang'
import { IBigPlank, PartType } from '@/partTypes'
import { toDecimal } from '@/util/commonFuncs'
import {
  getEdgeLengthsFromLShape,
  isSpecialShapeBigPlank,
  isSurplusBigPlank,
} from '@/util/plankCommonFuncs'
import { getPaibanTime } from '@/util/tag'
import { getCustomRecordKeys } from '@/views/fileManage/components/m-custom-folder/data'
import {
  File,
  FileType,
  Folder,
} from '@/views/fileManage/components/m-custom-folder/types'
import { base64ToBlob } from '@yige/utils'
import { nanoid } from 'nanoid'

import { sanitizeFileName } from '../util'
import { ContextCollector, FolderStructureBuilder } from './index'
import { FileSource, FolderNode, IEnricherParams, IExtraData } from './types'

/** @description 创建一个基于根目录的临时文件夹节点，用于其他类型的文件快速存入 */
export function createTemporaryRootFolder(
  name: string,
  supportedType: FileType
): Folder {
  return {
    id: nanoid(),
    name,
    nameFields: [
      {
        type: 'text',
        value: name,
      },
    ],
    nameLimitSize: 1000,
    pid: 'root',
    children: [],
    isFolder: true,
    startNum: 1,
    accept: {
      classifyChains: [],
      supportedFiles: [
        {
          type: supportedType,
          isFile: true,
          ext: '',
          id: '',
          name: '',
          pid: 'root',
        },
      ],
    },
    metadata: {},
  }
}
/** @description 用于为收集好的文件添加自定义的参数,将 nc 部分数据分发到文件上 */
export function enrichFileWithDefaultPlankData(params: IEnricherParams) {
  const { originNcData, parts, ncSetting, extraData, file } = params
  const syncKeys = getCustomRecordKeys()
  const { findId } = file
  const findTargetPart = () => {
    const ids = Array.isArray(findId) ? findId : [findId]
    const result: [PartType[] | undefined, IBigPlank[] | undefined] = [
      undefined,
      undefined,
    ]
    if (!ids.length) return result
    let targetBigPlanks = originNcData.filter((item) =>
      ids.includes(item.bigPlankUniqueId)
    )
    // 优先取小板信息在目标大板中获取
    let targetParts = targetBigPlanks.flatMap((item) => item.parts)
    // 如果找不到大板，或者文件是属于小板的，则在所有的文件中寻找
    if (!targetParts.length) {
      targetParts = parts.filter((v) => ids.includes(v.partUniqueId))
    }
    // 如果一开始就没找到对应大板则信息在已经找到的小板 去找对应的所属大板
    if (!targetBigPlanks.length) {
      targetBigPlanks = originNcData.filter((item) =>
        item.parts.some((v: PartType) => ids.includes(v.partUniqueId))
      )
    }
    result[0] = targetParts
    result[1] = targetBigPlanks
    return result
  }
  const target = findTargetPart().filter(Boolean) as ReturnType<
    typeof findTargetPart
  >
  if (!target.length) return
  /** @description 获取key 对应的 value */
  const getValue = (data: ReturnType<typeof findTargetPart>, key: string) => {
    // 现在小板中取 key，取不到 key 则在 大板中取
    const parts = data[0] ?? []
    const bigPlanks = data[1] ?? []
    const v1 = parts.map((part) => part[key as keyof PartType]).filter(Boolean)
    if (v1.length) {
      return Array.from(new Set(v1))
    }
    const v2 = bigPlanks
      .map((bigPlank) => bigPlank[key as keyof IBigPlank])
      .filter(Boolean)
    if (v2.length) {
      return Array.from(new Set(v2))
    }
  }
  const customRecord = syncKeys.reduce((record, key) => {
    // 如果某个字段的数据需要经过处理后才有值，则运行特殊函数的返回值，否则直接在板件上取值
    const specialFieldFn = specialFields[key as keyof typeof specialFields]
    const value: string | string[] | undefined = specialFieldFn
      ? specialFieldFn({ key, target, ncSetting, extraData, originNcData })
      : getValue(target, key)
    record[key] = value ?? ''
    return record
  }, {} as Record<string, string | string[]>)
  file.customRecord = Object.assign(file.customRecord ?? {}, customRecord)
}
/** 标准处理中的一些特殊字段需要重新计算数据 */
const specialFields: Record<string, (...args: any[]) => string> = {
  plankWidth: getPlankSize,
  plankHeight: getPlankSize,
  bigPlankType: getPlankType,
  productionLineName: getProductionLineName,
  paibanTime: () => sanitizeFileName(getPaibanTime(), '_'),
  outputTime: () =>
    sanitizeFileName(ContextCollector.InitTime ?? getPaibanTime(true), '_'),
  /** 因为不能第一时间知道自增序号，需要先组合文件后才能知晓，所以先保留 序号的格式 {order} 后续再做替换 */
  order: ({ key }) => `{${key}}`,
  singleCanvasIndex: getSingleCanvasIndex,
}

/** @description 获取板件尺寸数据字符串 */
function getPlankSize(params: {
  key: 'plankWidth' | 'plankHeight'
  target: [PartType[], IBigPlank[]]
  ncSetting: INcSetting
}) {
  const { key, target, ncSetting } = params
  const [_, bigPlanks] = target
  const bigPlank = bigPlanks?.[0]
  if (!bigPlank) return ''
  const { path = [], surplusInfo, plankWidth, plankHeight } = bigPlank ?? {}
  let width = String(plankWidth ?? '')
  let height = String(plankHeight ?? '')
  const { shape } = surplusInfo ?? {}
  /** 是否是余料 */
  const isSurplus = isSurplusBigPlank(bigPlank)
  const isLSurplus =
    shape === 'lshape' ||
    (isSurplus &&
      surplusInfo?.path &&
      [6, 7].includes(surplusInfo?.path.length))
  /** L 型余料板件尺寸信息显示样式不同 */
  if (surplusInfo && isLSurplus) {
    const {
      longestHorizontalEdge,
      secondLongestHorizontalEdge,
      longestVerticalEdge,
      secondLongestVerticalEdge,
    } = getEdgeLengthsFromLShape(path)
    width = `${toDecimal(longestHorizontalEdge.size, 2)}(${toDecimal(
      secondLongestHorizontalEdge.size,
      2
    )})`
    height = `${toDecimal(longestVerticalEdge.size, 2)}(${toDecimal(
      secondLongestVerticalEdge.size,
      2
    )})`
  }
  const result = [width, height]
  if (ncSetting?.xyReverse) {
    result.reverse()
  }
  return key === 'plankWidth' ? result[0] : result[1]
}
/** @description 获取板件类型数据字符串 */
function getPlankType(params: { target: [PartType[], IBigPlank[]] }) {
  const { target } = params
  const planks = target[1] ?? target[0]
  const plank = (Array.isArray(planks) ? planks[0] : planks) as IBigPlank
  const isSurplus = isSurplusBigPlank(plank)
  const isSpecialShape = isSpecialShapeBigPlank(plank)
  if (plank === undefined || plank === null) return ''
  return `${
    isSpecialShape
      ? translateLang('特殊')
      : isSurplus
      ? translateLang('余料')
      : translateLang('常规')
  }${translateLang('板')}`
}
/** @description 获取生产线名称 */
function getProductionLineName(params: { extraData: IExtraData }) {
  const { extraData } = params
  return extraData.productionLineName
}

/** @description 获取只有单张大板时显示的数据 */
function getSingleCanvasIndex(params: {
  originNcData: IBigPlank[]
  extraData: IExtraData
}) {
  let result = ''
  const { originNcData, extraData } = params
  if (extraData.downloadEnv !== 'single') return result
  const len = Number(originNcData?.length)
  if (len !== 1) return result
  const plank = originNcData?.[0]
  result = `大板${plank?.canvasIndex ?? 'unknown'}`
  return result
}

/**
 * @description 用于替换花括号中的内容
 * @param text 需要替换的文本
 * @param data 替换的数据
 * @returns 替换后的文本
 */
export function replaceTemplate(
  text: string,
  data: Record<string, string | number> | undefined
) {
  return text.replace(/\{([^}]+)\}/g, (_, key) => String(data?.[key] ?? ''))
}

/** @description 当前节点下的自节点文件名是否存在递增序号 */
export function isFolderNameExistStartNum(folder: FolderNode) {
  const childFolders = folder.children.filter(
    (child): child is FolderNode => child.type === 'folder'
  )
  return childFolders.some((child) => isNeedGenStartNum(child.config))
}
/**  @description 文件夹名称是否存在递增序号 */
export function isNeedGenStartNum(folder: Folder) {
  return folder.nameFields.find((field) => field.value === '{order}')
}
/**
 * @description 创建未分类文件夹 此数据直接可直接添加到 root 下进行处理
 * @returns
 */
export function createNotClassFolder(label = '未分类文件'): Folder {
  const folder = createTemporaryRootFolder(label, 'all')
  return folder
}

/** 将 raw 转为统一的 Blob（url 由 downloadFiles方法 写入 file） */
export function ensureFile(file: FileSource) {
  if (file.file) return
  switch (file.raw.kind) {
    case 'url':
      break
    case 'base64':
      file.file = base64ToBlob(file.raw.value)
      break
    case 'text':
      file.file = new Blob([file.raw.value], { type: 'text/plain' })
      break
    case 'blob':
      file.file = file.raw.value
      break
    case 'arrayBuffer':
      file.file = new Blob([file.raw.value])
      break
  }
}

/**
 * @description 当前文件夹模板或任意层子文件夹模板中，只要有一处 supportedFiles 支持该类型即视为支持
 * @param folder 文件夹模板（含 children 子树）
 * @param type 文件类型
 * @returns 是否支持
 */
export function isSupportedFile(
  folder: Folder,
  type: FileType,
  groupId: string
): boolean {
  const isSupported = getSupportedFile(
    folder.accept?.supportedFiles ?? [],
    type
  )
  // 还需要考虑后端传过来的分组 ID 是否和文件夹id相同，存在 分组 ID 的情况下才需要检查
  const isGroupIdSome = groupId ? groupId === folder.id : true
  if (isSupported && isGroupIdSome) {
    return true
  }
  const childFolders = folder.children ?? []
  return childFolders.some((child) => isSupportedFile(child, type, groupId))
}
/**
 * @description 文件组中找到指定的类型，或者支持所有类型文件
 * @param supportedFiles 支持的文件类型
 * @param type 文件类型
 * @returns 是否支持
 */
export function getSupportedFile(supportedFiles: File[], type: FileType) {
  return supportedFiles.find(
    (supportedFile) =>
      supportedFile.type === type || supportedFile.type === 'all'
  )
}

/**
 * @description 获取 zip 内文件相对路径（去掉根目录前缀）
 * isFullZip=true 时保留完整路径；根路径为空时不做截取，避免误删首字符
 */
export function getZipEntryPath(
  fullPath: string,
  rootPath: string,
  isFullZip: boolean
): string {
  if (isFullZip || !rootPath) return fullPath
  const sep = FolderStructureBuilder.sep
  const normalizedRoot = rootPath.endsWith(sep)
    ? rootPath.slice(0, -sep.length)
    : rootPath
  if (!normalizedRoot) return fullPath
  if (fullPath === normalizedRoot) return ''
  const prefix = `${normalizedRoot}${sep}`
  if (fullPath.startsWith(prefix)) {
    return fullPath.slice(prefix.length)
  }
  return fullPath
}
